<template>
  <div class="stock-page">
    <!-- Header -->
    <div class="stk-header">
      <div>
        <span class="header-eyebrow">Inventario</span>
        <h2 class="header-title">Stock de Materiales</h2>
      </div>
      <div class="header-actions">
        <div class="view-toggle">
          <button :class="['view-toggle__btn', { active: typeList }]" @click="typeList = true">
            <i class="fa-solid fa-list"></i>
          </button>
          <button :class="['view-toggle__btn', { active: !typeList }]" @click="typeList = false">
            <i class="fa-solid fa-grip"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- KPI Strip -->
    <div class="kpi-strip">
      <div class="kpi-card">
        <span class="kpi-label">Total</span>
        <span class="kpi-value">{{ listMaterialesAll.length }}</span>
        <span class="kpi-foot">materiales</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Sin stock</span>
        <span class="kpi-value kpi-value--danger">{{ countSinStock }}</span>
        <span class="kpi-foot">stock = 0</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Pendientes</span>
        <span class="kpi-value kpi-value--warning">{{ countPendientes }}</span>
        <span class="kpi-foot">por pedir</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Mostrando</span>
        <span class="kpi-value">{{ listMateriales.length }}</span>
        <span class="kpi-foot">resultados</span>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-bar">
      <div class="search-box">
        <i class="fa-solid fa-magnifying-glass search-box__icon"></i>
        <input type="text" class="search-box__input" placeholder="Buscar material..." v-model="buscar" />
      </div>

      <div class="filter-group">
        <label class="filter-label">Categoria</label>
        <v-select
          v-model="categoriaSeleccionada"
          :options="listCategorias"
          placeholder="Todas"
          class="select-styled select-filter"
        />
      </div>

      <div class="filter-group">
        <label class="filter-label">Ordenar</label>
        <div class="sort-row">
          <v-select
            v-model="ordenarSelect"
            :options="ordenarListado"
            placeholder="Seleccionar"
            class="select-styled select-filter"
          />
          <div class="sort-arrows">
            <button :class="['sort-arrow', { active: orderDirection }]" @click="orderDirection = true">
              <i class="fa-solid fa-chevron-up"></i>
            </button>
            <button :class="['sort-arrow', { active: !orderDirection }]" @click="orderDirection = false">
              <i class="fa-solid fa-chevron-down"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="filter-toggles">
        <button :class="['toggle-pill', { active: checkPendiente }]" @click="checkPendiente = !checkPendiente">
          <i class="fa-solid fa-clock"></i> Pendientes
        </button>
        <button :class="['toggle-pill', { active: checkEsperando }]" @click="checkEsperando = !checkEsperando">
          <i class="fa-solid fa-truck"></i> Esperando
        </button>
      </div>
    </div>

    <!-- Table view -->
    <div v-if="typeList" class="list-card">
      <div class="table-wrapper">
        <b-table
          :items="listMateriales"
          :fields="columnas"
          class="mb-0"
          hover
          sort-icon-left
          responsive
          show-empty
          empty-text="No se encontraron materiales"
          thead-class="table-head"
          tbody-tr-class="table-row"
          @row-clicked="openMaterial"
        >
          <template #cell(stock)="data">
            <span :class="[
              'stock-pill',
              (!data.item.stock && data.item.stock !== 0) ? 'stock-pill--neutral' :
              data.item.stock <= 0 ? 'stock-pill--danger' :
              data.item.pedir ? 'stock-pill--warning' : 'stock-pill--success'
            ]">
              {{ (data.item.stock != null && data.item.stock !== '') ? data.item.stock : '-' }}
            </span>
          </template>

          <template #cell(nombre)="data">
            <div class="cell-nombre">
              <span class="cell-nombre__name">{{ data.item.nombre }}</span>
              <span class="cell-nombre__sub" v-if="data.item.color">{{ data.item.color }}</span>
            </div>
          </template>

          <template #cell(material)="data">
            <span class="cell-text" v-if="data.item.material">{{ data.item.material }}</span>
            <span class="cell-muted" v-else>--</span>
          </template>

          <template #cell(medidas)="data">
            <span class="cell-mono" v-if="data.item.medidas">{{ data.item.medidas }}</span>
            <span class="cell-muted" v-else>--</span>
          </template>

          <template #cell(proveedor)="data">
            <span class="cell-text" v-if="data.item.proveedor">{{ data.item.proveedor }}</span>
            <span class="cell-muted" v-else>--</span>
          </template>
        </b-table>
      </div>
      <div class="list-card__footer">
        <span class="footer-count">{{ listMateriales.length }} materiales</span>
      </div>
    </div>

    <!-- Grid view -->
    <div v-else class="grid-view">
      <div
        v-for="(item, index) in listMateriales"
        :key="index"
        :class="[
          'grid-card',
          {
            'grid-card--warning': item.pedir,
            'grid-card--success': !item.pedir && item.stock > 0,
            'grid-card--danger': !item.pedir && item.stock <= 0,
          }
        ]"
        @click="openMaterial(item)"
      >
        <div class="grid-card__top">
          <h6 class="grid-card__title">{{ item.nombreYColor }}</h6>
          <span :class="[
            'stock-pill stock-pill--sm',
            (!item.stock && item.stock !== 0) ? 'stock-pill--neutral' :
            item.stock <= 0 ? 'stock-pill--danger' :
            item.pedir ? 'stock-pill--warning' : 'stock-pill--success'
          ]">
            {{ (item.stock != null && item.stock !== '') ? item.stock : '-' }}
          </span>
        </div>
        <div class="grid-card__meta" v-if="item.pedir">
          <span class="grid-card__pedir">Pedir: <strong>{{ item.cantidadPedir }}</strong></span>
        </div>
        <div class="grid-card__meta" v-if="item.fechaActualizacion != null">
          <span class="grid-card__date">{{ item.fechaActualizacion }}</span>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <b-modal
      ref="modal-cantidad"
      modal-class="modal-stk"
      centered
      hide-header
      hide-footer
      size="sm"
    >
      <div class="modal-stk-inner">
        <!-- Header with stock badge -->
        <div class="mstk-header">
          <div class="mstk-header__top">
            <span :class="[
              'mstk-stock-badge',
              (!materialSeleccionado.stock && materialSeleccionado.stock !== 0) ? 'mstk-stock-badge--neutral' :
              materialSeleccionado.stock <= 0 ? 'mstk-stock-badge--danger' :
              materialSeleccionado.pedir ? 'mstk-stock-badge--warning' : 'mstk-stock-badge--success'
            ]">
              {{ (materialSeleccionado.stock != null && materialSeleccionado.stock !== '') ? materialSeleccionado.stock : '0' }}
            </span>
          </div>
          <h4 class="mstk-header__title">{{ materialSeleccionado.nombre }}</h4>
          <p class="mstk-header__sub" v-if="materialSeleccionado.color">{{ materialSeleccionado.color }}</p>
        </div>

        <!-- Info chips -->
        <div class="mstk-chips">
          <span class="mstk-chip" v-if="materialSeleccionado.categoria">
            <i class="fa-solid fa-folder"></i> {{ materialSeleccionado.categoria }}
          </span>
          <span class="mstk-chip" v-if="materialSeleccionado.material">
            <i class="fa-solid fa-layer-group"></i> {{ materialSeleccionado.material }}
          </span>
          <span class="mstk-chip" v-if="materialSeleccionado.proveedor">
            <i class="fa-solid fa-truck"></i> {{ materialSeleccionado.proveedor }}
          </span>
        </div>

        <!-- Compact info rows -->
        <div class="mstk-info" v-if="materialSeleccionado.medidas || materialSeleccionado.ean || materialSeleccionado.ref">
          <div class="mstk-info__row" v-if="materialSeleccionado.medidas">
            <span class="mstk-info__label">Medidas</span>
            <span class="mstk-info__value">{{ materialSeleccionado.medidas }}</span>
          </div>
          <div class="mstk-info__row" v-if="materialSeleccionado.ean">
            <span class="mstk-info__label">EAN</span>
            <span class="mstk-info__value">{{ materialSeleccionado.ean }}</span>
          </div>
          <div class="mstk-info__row" v-if="materialSeleccionado.ref">
            <span class="mstk-info__label">Ref</span>
            <span class="mstk-info__value">{{ materialSeleccionado.ref }}</span>
          </div>
          <div class="mstk-info__row" v-if="materialSeleccionado.nota">
            <span class="mstk-info__label">Nota</span>
            <span class="mstk-info__value">{{ materialSeleccionado.nota }}</span>
          </div>
        </div>

        <!-- Modo normal: editar stock -->
        <div v-if="!checkEsperando" class="mstk-actions">
          <span class="mstk-actions__title">Stock actual</span>
          <div v-if="Number(materialSeleccionado.unidadesAlmacen) > 0" class="mstk-almacen-note">
            <i class="fa-solid fa-warehouse"></i>
            Este material tiene unidades en almacén: el stock se gestiona desde
            <router-link to="/almacen">Almacén</router-link>.
          </div>
          <template v-else>
          <div class="qty-row">
            <button class="qty-btn qty-btn--minus" @click="stockMaterial--">
              <i class="fa-solid fa-minus"></i>
            </button>
            <input type="number" v-model="stockMaterial" class="qty-input" @wheel.prevent />
            <button class="qty-btn qty-btn--plus" @click="stockMaterial++">
              <i class="fa-solid fa-plus"></i>
            </button>
          </div>
          <button class="mstk-btn mstk-btn--primary" @click="saveStock()">
            <i class="fa-solid fa-floppy-disk"></i> Guardar stock
          </button>
          </template>

          <div class="mstk-divider"></div>

          <!-- Pedir -->
          <div v-if="!materialSeleccionado.pedir">
            <span class="mstk-actions__title">Marcar como pedido</span>
            <div class="mstk-pedir-row">
              <input
                type="number"
                v-model="materialSeleccionado.cantidadPedir"
                class="qty-input qty-input--sm"
                placeholder="Ud"
                @wheel.prevent
              />
              <button class="mstk-btn mstk-btn--warning" @click="savePedir(true)">
                <i class="fa-solid fa-clock"></i> Pedir
              </button>
            </div>
          </div>
          <div v-else>
            <button class="mstk-btn mstk-btn--outline-danger mstk-btn--block" @click="savePedir(false)">
              <i class="fa-solid fa-xmark"></i> Cancelar pedido ({{ materialSeleccionado.cantidadPedir }})
            </button>
          </div>
        </div>

        <!-- Modo esperando recibir -->
        <div v-else class="mstk-actions">
          <div class="mstk-esperando">
            <span class="mstk-esperando__label">Esperando</span>
            <span class="mstk-esperando__value">{{ materialSeleccionado.cantidadEsperando }}</span>
          </div>

          <span class="mstk-actions__title">Cantidad recibida</span>
          <div class="qty-row">
            <button class="qty-btn qty-btn--minus" @click="cantidadRecibida--">
              <i class="fa-solid fa-minus"></i>
            </button>
            <input type="number" v-model="cantidadRecibida" class="qty-input" @wheel.prevent />
            <button class="qty-btn qty-btn--plus" @click="cantidadRecibida++">
              <i class="fa-solid fa-plus"></i>
            </button>
          </div>
          <button class="mstk-btn mstk-btn--primary mstk-btn--block" @click="guardarRecibido()">
            <i class="fa-solid fa-check"></i> Guardar recibido
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'

export default {
  components: {
    vSelect
  },
  data(){
    return{
      typeList: false,
      listMateriales: [],
      listMaterialesAll: [],
      nombreMaterialSeleccionado: "",
      stockMaterial: "",
      cantidadRecibida: "",
      cantidadPedir: "",
      materialSeleccionado: {nombre:''},
      ordenarSelect: null,
      checkPendiente: false,
      checkEsperando: false,
      ordenarListado: ["Nombre","Stock","Material","Proveedor","Categoria"],
      listCategorias: ["Fidlocks","Imanes","Herrajes","Cremalleras","Cursores","Tintes","Pegamentos","Cintas refuerzo","Fieltro","Microfibra","RPET","Tejidos","Refuerzos","Cuero","Cintas de mochila"],
      orderDirection: true,
      categoriaSeleccionada: null,
      buscar: "",
      columnas: [
        { key: "stock", label: "Stock", sortable: true, thStyle: { width: "80px" } },
        { key: "nombre", label: "Material", sortable: true },
        { key: "material", label: "Tipo", sortable: true },
        { key: "medidas", label: "Medidas", sortable: true, thStyle: { width: "90px" } },
        { key: "proveedor", label: "Proveedor", sortable: true },
      ],
      listPedidosRecibir: []
    }
  },
  computed: {
    countSinStock() {
      return this.listMaterialesAll.filter(m => !m.stock || m.stock <= 0).length
    },
    countPendientes() {
      return this.listMaterialesAll.filter(m => m.pedir).length
    }
  },
  methods:{
    loadItems()
    {
      axios.post("/uso", {seccion:"stock"});
      axios
        .get("/materiales", { params: { sinImg: 1 } })
        .then(response => {
            return response.data;
        })
        .then(res => {
          this.listMaterialesAll = res;
          this.listMaterialesAll = res.map(material => ({
              ...material,
              stock: (material.stock !== 'null' && material.stock !== '') ? material.stock : 0,
              nombreYColor: material.color !== '' ? material.nombre + ' - ' + material.color : material.nombre
          }));
          this.listMateriales = this.listMaterialesAll;
        });

        axios
        .get("/materiales/pedidos/recibir")
        .then(response => {
            return response.data;
        })
        .then(res => {
          this.listPedidosRecibir = res;
        });
    },
    openMaterial(data){
      this.materialSeleccionado = data;
      this.nombreMaterialSeleccionado = data.nombre + " " + data.color;
      this.stockMaterial = data.stock;
      if(this.stockMaterial == 0)
        this.stockMaterial = "";

      if(this.materialSeleccionado.cantidadPedir == null)
        this.materialSeleccionado.cantidadPedir = 1;

      this.cantidadRecibida = data.cantidadRecibida;
      this.$refs['modal-cantidad'].show()
    },
    saveStock(){
      axios
        .post("/materiales/stock/update", {stock: this.stockMaterial, id: this.materialSeleccionado.id})
        .then(response => {
            return response.data;
        })
        .then(res => {
          this.materialSeleccionado.stock = this.stockMaterial;
          this.listMaterialesAll.forEach(material => {
            if(material.id == this.materialSeleccionado.id)
              material.stock = this.stockMaterial;
          });
          this.buscarOrdernar();
          this.$refs['modal-cantidad'].hide()
        });
    },
    savePedir(estado){
      axios
        .put("/materiales/pedir", {pedir: estado, id: this.materialSeleccionado.id, cantidadPedir: this.materialSeleccionado.cantidadPedir})
        .then(response => {
            return response.data;
        })
        .then(res => {
          this.listMaterialesAll.forEach(material => {
            if(material.id == this.materialSeleccionado.id)
              material.pedir = estado;
          });

          this.buscarOrdernar();
          this.$refs['modal-cantidad'].hide()
        });
    },
    buscarOrdernar(){
      this.listMateriales = [];
      let auxDatos = JSON.parse(JSON.stringify(this.listMaterialesAll));

      if(this.buscar == "" && this.categoriaSeleccionada == null && !this.checkPendiente)
        this.listMateriales = auxDatos;
      else
      {
        auxDatos.forEach(material => {
          if(material.nombre.toLowerCase().includes(this.buscar.toLowerCase()) ||
            material.categoria.toLowerCase().includes(this.buscar.toLowerCase())
          )
          {
            if(!this.checkPendiente)
              {
                if(this.categoriaSeleccionada == null || material.categoria == this.categoriaSeleccionada)
                  this.listMateriales.push(material);
              }
              else
              {
                if((this.categoriaSeleccionada == null || material.categoria == this.categoriaSeleccionada) && material.pedir)
                  this.listMateriales.push(material);
              }
          }
        });
      }

      if(this.ordenarSelect != null)
      {
        let sortProp = this.ordenarSelect.toLowerCase();
        if(sortProp != "stock")
        {
          if(this.orderDirection)
            this.listMateriales.sort((a,b) => b[sortProp].localeCompare(a[sortProp]));
          else
            this.listMateriales.sort((a,b) => a[sortProp].localeCompare(b[sortProp]));
        }
        else
        {
          if(this.orderDirection)
            this.listMateriales.sort((a,b) => b[sortProp] - a[sortProp]);
          else
            this.listMateriales.sort((a,b) => a[sortProp] - b[sortProp]);
        }
      }
    },
    cargarEsperando(){
      if(this.checkEsperando)
      {
        this.listMateriales = [];

        this.listPedidosRecibir.forEach(pedido => {
          pedido.listaMateriales.forEach(material => {
            if (!material.recibido) {
              const matchingMaterial = this.listMaterialesAll.filter(m => m.id === material.id);
              if (matchingMaterial.length > 0 && !this.listMateriales.includes(matchingMaterial[0])) {
                matchingMaterial[0].idPedido = pedido.id;
                matchingMaterial[0].cantidadEsperando = material.cantidad;
                matchingMaterial[0].cantidadRecibida = material.cantidadRecibida;
                this.listMateriales.push(matchingMaterial[0]);
              }
            }
          });
        });
      }
      else
      {
        this.buscarOrdernar();
      }
    },
    guardarRecibido(){
      const pedidoMaterialIndex = this.listPedidosRecibir.findIndex(pedido => pedido.id === this.materialSeleccionado.idPedido);
      if (pedidoMaterialIndex !== -1) {
        const pedidoMaterial = this.listPedidosRecibir[pedidoMaterialIndex];
        const materialIndex = pedidoMaterial.listaMateriales.findIndex(material => material.id === this.materialSeleccionado.id);
        if (materialIndex !== -1) {
          const material = pedidoMaterial.listaMateriales[materialIndex];
          material.cantidadRecibida = this.cantidadRecibida;
          material.recibido = this.cantidadRecibida > 0;

          const nuevoStock = parseFloat(this.materialSeleccionado.stock) + parseFloat(material.cantidadRecibida);

          // Con unidades de almacén la entrada se registra en /almacen, no aquí
          if (Number(this.materialSeleccionado.unidadesAlmacen) > 0)
            this.$swal({
              icon: 'info',
              title: 'Registrar en Almacén',
              text: `${this.materialSeleccionado.nombre} tiene unidades en almacén: añade las ${material.cantidadRecibida} recibidas desde /almacen.`,
              customClass: { confirmButton: 'btn btn-primary' },
            });
          else
          axios
          .post("/materiales/stock/update", {stock: nuevoStock, id: material.id})
          .then(response => {
              return response.data;
          })
          .then(res => {
            this.materialSeleccionado.stock = nuevoStock;
            this.listMaterialesAll.forEach(material => {
              if(material.id == this.materialSeleccionado.id)
                material.stock = nuevoStock;
            });
          });
        }

        const fechaActual = new Date();
        const dia = fechaActual.getDate().toString().padStart(2, '0');
        const mes = (fechaActual.getMonth() + 1).toString().padStart(2, '0');
        const anio = fechaActual.getFullYear().toString();

        let fechaParseada = `${anio}-${mes}-${dia}`;

        const pedidoRecibido = pedidoMaterial.listaMateriales.every(material => material.recibido);

        if (pedidoRecibido) {
          pedidoMaterial.fechaRecibido = fechaParseada;
        }

        pedidoMaterial.lista = JSON.stringify(pedidoMaterial.listaMateriales);

        axios
        .put("/materiales/pedidos/recibido", pedidoMaterial)
        .then(response => {
            return response.data;
        })
        .then(res => {
          this.$swal({
                  icon: 'success',
                  title: 'El material se ha actualizado correctamente' ,
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                  timerProgressBar: true,
              })
        });
      }

      this.cargarEsperando();
      this.$refs['modal-cantidad'].hide();
    }
  },
  watch: {
    orderDirection(){ this.buscarOrdernar(); },
    ordenarSelect(){ this.buscarOrdernar(); },
    buscar(){ this.buscarOrdernar(); },
    categoriaSeleccionada(){ this.buscarOrdernar(); },
    checkPendiente(){ this.buscarOrdernar(); },
    checkEsperando(){ this.cargarEsperando(); }
  },
  mounted() {
    this.loadItems();
  },
}
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$primary-dark: #2563EB;
$success: #10B981;
$danger: #EF4444;
$warning: #F59E0B;
$info: #06B6D4;
$border: #E2E8F0;
$text: #1E293B;
$text-muted: #64748B;
$bg: #F8FAFC;
$card-bg: #FFFFFF;
$radius: 12px;
$radius-sm: 8px;
$shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);

.stock-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 8px;
}

// Header
.stk-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
}

.header-eyebrow {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 2px;
}

.header-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: $text;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}

// View toggle
.view-toggle {
  display: flex;
  background: $bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 3px;

  &__btn {
    border: none;
    background: transparent;
    padding: 6px 10px;
    border-radius: 6px;
    cursor: pointer;
    color: $text-muted;
    font-size: 14px;
    transition: all 0.15s ease;

    &.active {
      background: $card-bg;
      color: $primary;
      box-shadow: 0 1px 3px rgba(0,0,0,0.08);
    }

    &:hover:not(.active) { color: $text; }
  }
}

// KPI Strip
.kpi-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.kpi-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  box-shadow: $shadow;
}

.kpi-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
}

.kpi-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 22px;
  font-weight: 700;
  color: $text;
  font-feature-settings: "tnum";
  margin: 4px 0 2px;

  &--danger { color: $danger; }
  &--warning { color: $warning; }
}

.kpi-foot {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: $text-muted;
}

// Filters bar
.filters-bar {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  padding: 16px 20px;
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 300px;

  &__icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: $text-muted;
    font-size: 12px;
  }

  &__input {
    width: 100%;
    padding: 7px 12px 7px 32px;
    border: 1px solid $border;
    border-radius: $radius-sm;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    color: $text;
    background: transparent;
    transition: border-color 0.15s ease;

    &::placeholder { color: #CBD5E1; }
    &:focus { outline: none; border-color: $primary; }
  }
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: $text-muted;
  margin: 0;
}

.sort-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.sort-arrows {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.sort-arrow {
  border: none;
  background: transparent;
  padding: 0 4px;
  cursor: pointer;
  color: #CBD5E1;
  font-size: 10px;
  transition: color 0.15s ease;

  &.active { color: $primary; }
  &:hover:not(.active) { color: $text-muted; }
}

.filter-toggles {
  display: flex;
  gap: 6px;
  margin-left: auto;
}

.toggle-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 20px;
  border: 1px solid $border;
  background: transparent;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: $primary;
    color: $primary;
  }

  &.active {
    background: $primary;
    border-color: $primary;
    color: white;
  }

  i { font-size: 11px; }
}

// List Card
.list-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  overflow: hidden;

  &__footer {
    padding: 12px 22px;
    border-top: 1px solid $border;
  }
}

.table-wrapper {
  padding: 0;
}

// Los ::v-deep van bajo .stock-page a proposito: en la raiz, postcss-rtl + scoped generan [dir][data-v-xxx]
// y ninguna propiedad direccional (padding, margin...) llega a aplicarse.
.stock-page ::v-deep .table-head th {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
  border-top: none;
  border-bottom: 1px solid $border;
  padding: 10px 16px;
  background: transparent;
}

.stock-page ::v-deep .table-row td {
  padding: 10px 16px;
  vertical-align: middle;
  border-color: lighten($border, 3%);
  font-size: 13px;
  cursor: pointer;
}

.stock-page ::v-deep .table-row:hover td {
  background: $bg;
}

.stock-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  padding: 3px 10px;
  border-radius: 20px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  font-feature-settings: "tnum";

  &--success { background: rgba($success, 0.1); color: $success; }
  &--warning { background: rgba($warning, 0.1); color: darken($warning, 10%); }
  &--danger { background: rgba($danger, 0.1); color: $danger; }
  &--neutral { background: rgba($text-muted, 0.08); color: $text-muted; }
  &--sm { font-size: 11px; padding: 2px 8px; }
}

.cell-nombre {
  display: flex;
  flex-direction: column;

  &__name {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    color: $text;
    line-height: 1.2;
  }

  &__sub {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: $text-muted;
  }
}

.cell-text { font-size: 13px; color: $text; }
.cell-mono { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: $text; font-feature-settings: "tnum"; }
.cell-muted { color: #CBD5E1; font-size: 12px; }

.footer-count {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text-muted;
}

// Grid view
.grid-view {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 12px;
}

.grid-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
    transform: translateY(-1px);
  }

  &--success { background: rgba($success, 0.03); border-color: rgba($success, 0.2); }
  &--warning { background: rgba($warning, 0.03); border-color: rgba($warning, 0.2); }
  &--danger { background: rgba($danger, 0.03); border-color: rgba($danger, 0.2); }

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
  }

  &__title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: $text;
    margin: 0;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &__meta {
    margin-top: 6px;
  }

  &__pedir {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: $warning;
  }

  &__date {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: $text-muted;
  }
}

// Responsive
@media (max-width: 768px) {
  .kpi-strip { grid-template-columns: repeat(2, 1fr); }
  .stk-header { flex-direction: column; align-items: flex-start; gap: 12px; }
  .filters-bar { flex-direction: column; align-items: stretch; }
  .search-box { max-width: none; }
  .filter-toggles { margin-left: 0; }
}
</style>

<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';

// Vue-select (unscoped)
.stock-page {
  .select-styled {
    .vs__dropdown-toggle {
      min-height: 33px;
      padding: 0 0 2px 0;
      border-radius: 8px;
      border: 1px solid #E2E8F0;
      transition: border-color 0.15s ease;
    }

    .vs__search {
      font-size: 13px;
      font-family: 'Plus Jakarta Sans', sans-serif;
    }

    &.vs--open .vs__dropdown-toggle {
      border-color: #3B82F6;
      box-shadow: 0 0 0 3px rgba(#3B82F6, 0.1);
    }
  }

  .select-filter { min-width: 160px; }
}

// Modal
.modal-stk {
  .modal-content {
    border-radius: 20px;
    border: 1px solid #E2E8F0;
    box-shadow: 0 25px 60px rgba(0,0,0,0.15), 0 10px 24px rgba(0,0,0,0.08);
    overflow: hidden;
  }

  .modal-body { padding: 0; }
}

.modal-stk-inner {
  padding: 28px;
}

// Modal header
.mstk-header {
  text-align: center;
  margin-bottom: 18px;

  &__top {
    margin-bottom: 12px;
  }

  &__title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 20px;
    font-weight: 800;
    color: #1E293B;
    margin: 0;
    line-height: 1.2;
  }

  &__sub {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: #94A3B8;
    margin: 4px 0 0;
  }
}

.mstk-stock-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  padding: 6px 16px;
  border-radius: 24px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 20px;
  font-weight: 800;
  font-feature-settings: "tnum";

  &--success { background: rgba(#10B981, 0.1); color: #10B981; }
  &--warning { background: rgba(#F59E0B, 0.1); color: #D97706; }
  &--danger { background: rgba(#EF4444, 0.1); color: #EF4444; }
  &--neutral { background: rgba(#64748B, 0.08); color: #94A3B8; }
}

// Chips
.mstk-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-bottom: 16px;
}

.mstk-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  background: #F1F5F9;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 500;
  color: #64748B;

  i { font-size: 9px; }
}

// Info rows
.mstk-info {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 4px 0;
  margin-bottom: 20px;

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 7px 14px;

    & + & { border-top: 1px solid #F1F5F9; }
  }

  &__label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94A3B8;
  }

  &__value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 500;
    color: #1E293B;
    text-align: right;
    max-width: 60%;
    word-break: break-all;
  }
}

// Actions section
.mstk-actions {
  text-align: center;

  &__title {
    display: block;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #64748B;
    margin-bottom: 12px;
  }
}

.mstk-almacen-note {
  margin: 6px 0 4px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(#3B82F6, 0.08);
  color: #1E40AF;
  font-size: 12px;
  line-height: 1.4;

  i { margin-right: 4px; }
  a { font-weight: 600; text-decoration: underline; }
}

.mstk-divider {
  height: 1px;
  background: #E2E8F0;
  margin: 20px 0;
}

// Qty controls
.qty-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.qty-btn {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  color: white;
  font-size: 15px;

  &--minus {
    background: #EF4444;
    box-shadow: 0 2px 8px rgba(#EF4444, 0.25);
    &:hover { background: darken(#EF4444, 8%); transform: scale(1.08); }
  }

  &--plus {
    background: #10B981;
    box-shadow: 0 2px 8px rgba(#10B981, 0.25);
    &:hover { background: darken(#10B981, 8%); transform: scale(1.08); }
  }
}

.qty-input {
  width: 90px;
  text-align: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 26px;
  font-weight: 800;
  border: 2px solid #E2E8F0;
  border-radius: 12px;
  padding: 8px;
  color: #1E293B;
  font-feature-settings: "tnum";
  background: transparent;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; }
  &:focus { outline: none; border-color: #3B82F6; box-shadow: 0 0 0 4px rgba(#3B82F6, 0.1); }

  &--sm { width: 70px; font-size: 18px; padding: 6px; }
}

// Buttons
.mstk-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 20px;
  border-radius: 10px;
  border: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &--block { width: 100%; }

  &--primary {
    background: #3B82F6;
    color: white;
    box-shadow: 0 2px 8px rgba(#3B82F6, 0.25);
    &:hover { background: #2563EB; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(#3B82F6, 0.3); }
  }

  &--warning {
    background: #F59E0B;
    color: white;
    box-shadow: 0 2px 8px rgba(#F59E0B, 0.25);
    &:hover { background: darken(#F59E0B, 8%); }
  }

  &--outline-danger {
    background: transparent;
    color: #EF4444;
    border: 1px solid rgba(#EF4444, 0.3);
    &:hover { background: rgba(#EF4444, 0.05); }
  }
}

// Pedir row
.mstk-pedir-row {
  display: flex;
  justify-content: center;
  gap: 10px;
  align-items: center;
}

// Esperando
.mstk-esperando {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(#F59E0B, 0.06);
  border: 1px solid rgba(#F59E0B, 0.2);
  border-radius: 10px;
  margin-bottom: 18px;

  &__label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: #92400E;
  }

  &__value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 20px;
    font-weight: 800;
    color: #92400E;
    font-feature-settings: "tnum";
  }
}

// Dark mode
.dark-layout {
  .stock-page {
    .header-title { color: #E2E8F0; }

    .kpi-card { background: #1E293B; border-color: #334155; }
    .kpi-value { color: #F1F5F9; }

    .view-toggle { background: #1E293B; border-color: #334155; }
    .view-toggle__btn { color: #94A3B8; &.active { background: #334155; color: #3B82F6; } }

    .filters-bar { background: #1E293B; border-color: #334155; }

    .search-box__input {
      border-color: #334155; color: #E2E8F0;
      &::placeholder { color: #475569; }
      &:focus { border-color: #3B82F6; }
    }

    .toggle-pill {
      border-color: #475569; color: #94A3B8;
      &:hover { border-color: #3B82F6; color: #3B82F6; }
      &.active { background: #3B82F6; border-color: #3B82F6; color: white; }
    }

    .sort-arrow { color: #475569; &.active { color: #3B82F6; } }

    .list-card { background: #1E293B; border-color: #334155; }
    .list-card__footer { border-color: #334155; }

    .table-head th { color: #94A3B8; border-color: #334155; }
    .table-row td { border-color: #334155; }
    .table-row:hover td { background: rgba(255,255,255,0.03); }

    .cell-nombre__name { color: #E2E8F0; }
    .cell-text { color: #E2E8F0; }
    .cell-mono { color: #E2E8F0; }

    .grid-card { background: #1E293B; border-color: #334155; &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.2); } }
    .grid-card__title { color: #E2E8F0; }

    .select-styled .vs__dropdown-toggle { border-color: #334155; background: #0F172A; }
    .select-styled .vs__search { color: #E2E8F0; }
  }

  .modal-stk .modal-content { background: #1E293B; border-color: #334155; }

  .mstk-header__title { color: #E2E8F0; }
  .mstk-header__sub { color: #64748B; }

  .mstk-chip { background: #334155; color: #94A3B8; }

  .mstk-info { background: #0F172A; border-color: #334155; }
  .mstk-info__row + .mstk-info__row { border-color: #1E293B; }
  .mstk-info__value { color: #E2E8F0; }

  .mstk-divider { background: #334155; }
  .mstk-actions__title { color: #94A3B8; }

  .qty-input { border-color: #334155; color: #E2E8F0; background: #0F172A; &:focus { border-color: #3B82F6; } }

  .mstk-esperando { background: rgba(#F59E0B, 0.08); border-color: rgba(#F59E0B, 0.15); }
  .mstk-esperando__label { color: #FCD34D; }
  .mstk-esperando__value { color: #FCD34D; }
}
</style>
